import { HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { getApp } from '@angular/fire/app';
import { collection, collectionData, deleteField, doc, docData, DocumentData, getDoc, getFirestore, query, serverTimestamp, where } from '@angular/fire/firestore';
import { getStorage, ref } from '@angular/fire/storage';
import { COMMONS_ENV, FirebaseObservableService, FirebaseObservableServiceFactory, HttpService } from '@damofire/data-angular-commons';
import { APP_CACHE_COLLECTION, dtoConverter, ID_FIELD_OPTIONS, Query, SALES_ORDER_COLLECTION, SALES_ORDER_MODELS_COLLECTION, SearchResult } from '@damofire/data-commons';
import { AuthDtoSignerService, AuthenticationService } from '@damofire/data-security';
import { QueryConstraint } from '@firebase/firestore';
import { getDownloadURL } from 'firebase/storage';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ModelItem, SALES_ORDER_STATUS_TYPES, SalesOrder, SalesOrderModel } from '..';

export abstract class ProductionSalesOrdersService {
  abstract getByJob(job: string): Promise<SalesOrder>;
  abstract search(q: Query): Observable<SearchResult<SalesOrder>>;
  abstract sync(): Promise<void>;
  abstract getModelItems(job: string): Observable<SalesOrderModel[]>;
  abstract getModel(job: string, model: string): Observable<SalesOrderModel>;
  abstract markDrawingsAsComplete(id: string): Promise<void>;
  abstract undoMarkDrawingsAsComplete(id: string): Promise<void>;
  abstract generatePdf(job: string): Promise<void>;
  abstract getPdf(job: string): Promise<string>;
  abstract getSalesOrderById(id: string): Observable<SalesOrder>;
  abstract canDeleteSalesOrder(job: string): Promise<boolean>;
  abstract deleteSalesOrder(id: string): Promise<void>;
  abstract searchItems(query: string, job: string): Promise<ModelItem[]>;
  abstract suggestItems(selectedItems: ModelItem[], job: string): Promise<ModelItem[]>;
  abstract $nonProducts: BehaviorSubject<string[]>;
}

@Injectable()
export class FirebaseProductionSalesOrdersService implements ProductionSalesOrdersService {
  private readonly service: FirebaseObservableService<SalesOrder>;
  readonly $nonProducts: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  constructor(
    readonly firebaseObservableServiceFactory: FirebaseObservableServiceFactory,
    private readonly http: HttpService,
    @Inject(COMMONS_ENV) private readonly environment: any,
    private readonly authService: AuthenticationService,
    private readonly dtoSigner: AuthDtoSignerService,
  ) {
    this.service = firebaseObservableServiceFactory.createService(SALES_ORDER_COLLECTION);
    this.getNonProductionItems().then((nonProducts) => {
      this.$nonProducts.next(nonProducts);
    });
  }

  async canDeleteSalesOrder(job: string): Promise<boolean> {
    const salesOrders = await this.getAllByJob(job);
    // If there are duplicate SO, allow deletion
    if (salesOrders.length > 1) {
      return true;
    }
    // If there are no models (drawings) associated to this SO, allow deletion.
    const models = await this.getModelItems(job).pipe(take(1)).toPromise();
    return models?.length === 0;
  }

  deleteSalesOrder(id: string): Promise<void> {
    console.log(`Deleting SalesOrder ${id}`);
    return this.http.delete(`df-salesOrder/${id}`);
  }

  getSalesOrderById(id: string): Observable<SalesOrder> {
    return docData<SalesOrder>(doc(getFirestore(), `${SALES_ORDER_COLLECTION}/${id}`), ID_FIELD_OPTIONS);
  }

  async getByJob(job: string): Promise<SalesOrder> {
    const salesOrders = await this.getAllByJob(job);
    return salesOrders?.length >= 1 ? salesOrders[0] : null;
  }

  search(q: Query): Observable<SearchResult<SalesOrder>> {
    const preFilters: QueryConstraint[] = [where('production', '==', true)];
    if (q.filtersOps) {
      q.filtersOps.forEach((filter) => preFilters.push(where(filter.field, filter.op, filter.value)));
      delete q.filtersOps;
    }

    if (q.filters) {
      Object.keys(q.filters).forEach((filterKey) => preFilters.push(where(filterKey, '==', q.filters[filterKey])));
      delete q.filters;
    }

    return this.service.observeCollectionSearch(q, ['customerName', 'description', 'job', 'po', 'endUser'], preFilters);
  }

  getModelItems(job: string): Observable<SalesOrderModel[]> {
    return new Observable<SalesOrderModel[]>((observer) => {
      const q = query(collection(getFirestore(), SALES_ORDER_MODELS_COLLECTION).withConverter(dtoConverter<SalesOrderModel>()), where('salesOrder.job', '==', job));
      firstValueFrom(collectionData(q)).then((soms) => observer.next(soms as SalesOrderModel[]));
    });
  }

  getModel(job: string, model: string): Observable<SalesOrderModel> {
    const wheres = [where('salesOrder.job', '==', job), where('model', '==', model)];
    const q = query(collection(getFirestore(), SALES_ORDER_MODELS_COLLECTION).withConverter(dtoConverter<SalesOrderModel>()), ...wheres);
    return collectionData(q, { idField: '_id' }).pipe(map((array) => array[0] as SalesOrderModel));
  }

  sync(): Promise<void> {
    return this.http.post(`df-salesOrder`);
  }

  generatePdf(job: string): Promise<void> {
    return this.http.get(`df-salesOrder/${job}/pdf`);
  }

  async markDrawingsAsComplete(id: string): Promise<void> {
    const user = await this.authService.currentUser();
    const data = {
      drawn: {
        date: serverTimestamp(),
        by: user._id,
      },
    };

    await this.dtoSigner.update(data, `${SALES_ORDER_COLLECTION}/${id}`);
  }

  async undoMarkDrawingsAsComplete(id: string): Promise<void> {
    const data = {
      drawn: deleteField(),
      status: SALES_ORDER_STATUS_TYPES.READY_TO_DRAW,
    };
    await this.dtoSigner.update(data, `${SALES_ORDER_COLLECTION}/${id}`);
  }

  async getPdf(path: string): Promise<string> {
    try {
      const bucket = getStorage(getApp(), this.environment.appConfig.production.bucket.drawings);
      return await getDownloadURL(ref(bucket, path));
    } catch (e) {
      return null;
    }
  }

  async searchItems(query: string, job: string): Promise<ModelItem[]> {
    const urlSafeQuery = encodeURIComponent(query);
    const params = new HttpParams({ fromObject: { query: urlSafeQuery, job } });
    return this.http.get(`df-items/search`, params);
  }

  async suggestItems(selectedItems: ModelItem[], job: string): Promise<ModelItem[]> {
    return this.http.post(`df-items/suggest`, { items: selectedItems, job });
  }

  private async getNonProductionItems(): Promise<string[]> {
    const cacheSnap = await getDoc(doc(getFirestore(), `${APP_CACHE_COLLECTION}/wh-products`));
    return cacheSnap.data()?.['nonProduction'] ?? [];
  }

  private async getAllByJob(job: string): Promise<SalesOrder[]> {
    const q = query<SalesOrder, DocumentData>(collection(getFirestore(), SALES_ORDER_COLLECTION), where('job', '==', job));
    const snaps = await collectionData(q, { idField: '_id' }).pipe(take(1)).toPromise();
    return snaps.length >= 1 ? snaps : null;
  }
}
